import { type CouponWithCampaignDataFragment } from '@/affiliate-dashboard/graphql';
import { Label } from '@/components/base/label';
import { cn } from '@/utils';
import { faFacebook, faXTwitter } from '@fortawesome/free-brands-svg-icons';
import { faCheck, faCopy, faTags } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { createColumnHelper } from '@tanstack/react-table';
import { type ReactNode, useState } from 'react';
import { type IntlShape } from 'react-intl';
import { useCopyToClipboard } from 'usehooks-ts';

const columnHelper =
  createColumnHelper<CouponWithCampaignDataFragment | null>();

export const defaultColumns = (
  intl: IntlShape,
  { t }: { t: (key: string) => string },
) => [
  columnHelper.accessor('couponCodeRequested', {
    id: 'couponCodeRequested	',
    header: () => t('table.YourCoupon'),
    cell: (info) => (
      <div className="flex items-center gap-2">
        <FontAwesomeIcon className="h-5 w-5" icon={faTags} />
        <span className="text-default-gray-950 dark:text-white">
          {info.getValue()}
        </span>
      </div>
    ),
    enableSorting: false,
  }),

  columnHelper.accessor('couponConfig', {
    id: 'couponConfig	',
    header: () => t('table.Amount'),
    cell: (props) => {
      const config: { amount?: number } = JSON.parse(
        props.getValue() ?? '',
      ) as { amount?: number };

      const couponDiscountProcessed =
        props.row.original?.couponDiscountProcessed ?? 0;
      const couponDiscountRequested =
        props.row.original?.couponDiscountRequested ?? 0;
      const isGlobalBigger = couponDiscountProcessed > couponDiscountRequested;

      return config.amount ? (
        <Label className="h-fit p-2 text-sm leading-3">
          <span className={cn({ 'opacity-50': isGlobalBigger })}>
            {intl.formatNumber((couponDiscountRequested ?? 0) / 100, {
              style: 'percent',
              maximumFractionDigits: 2,
            })}
          </span>
          {isGlobalBigger && (
            <span>
              {'->'}{' '}
              {intl.formatNumber((couponDiscountProcessed ?? 0) / 100, {
                style: 'percent',
                maximumFractionDigits: 2,
              })}
            </span>
          )}
        </Label>
      ) : (
        '-'
      );
    },
    enableSorting: false,
  }),
  // columnHelper.accessor('expiresAt', {
  //   id: 'expiresAt	',
  //   header: () => t('table.ExpiresAt'),
  //   cell: (props) => {
  //     return (
  //       <span>
  //         {props.getValue()
  //           ? format(props.getValue() ?? '', 'dd.MM.yyyy')
  //           : '-'}
  //       </span>
  //     );
  //   },
  //   enableSorting: false,
  // }),

  columnHelper.accessor('couponCodeRequested', {
    id: 'couponCodeRequested',
    header: () => t('table.ShareCoupon'),
    cell: (info) => {
      const code = info.getValue() ?? '';
      const shareUrl = encodeURIComponent(
        `https://app.monevis.com/order?promo=${code}`,
      );
      const facebookUrl = `https://www.facebook.com/sharer/sharer.php?u=${shareUrl}`;
      const twitterUrl = `https://twitter.com/intent/tweet?url=${shareUrl}&text=Check%20this%20out!`;

      return (
        <div className="flex justify-end gap-2">
          <Label variant="gray" className="flex items-center justify-center">
            <CopyComp className="h-4 w-4" copyText={code ?? ''}>
              <FontAwesomeIcon className="h-4 w-4" icon={faCopy} />
            </CopyComp>
          </Label>
          <a href={facebookUrl} target="_blank" rel="noopener noreferrer">
            <Label variant="gray" className="flex items-center justify-center">
              <FontAwesomeIcon className="h-4 w-4" icon={faFacebook} />
            </Label>
          </a>
          <a href={twitterUrl} target="_blank" rel="noopener noreferrer">
            <Label variant="gray" className="flex items-center justify-center">
              <FontAwesomeIcon className="h-4 w-4" icon={faXTwitter} />
            </Label>
          </a>
        </div>
      );
    },
    enableSorting: false,
  }),
];

function CopyComp({
  children,
  copyText,
  className,
}: {
  children: ReactNode;
  copyText: string;
  className?: string;
}): JSX.Element {
  const [_copiedText, copy] = useCopyToClipboard();
  const [copied, setCopied] = useState(false);

  function handleCopy(): void {
    return void (async () => {
      await copy(copyText);
      setCopied(true);
      setTimeout(() => {
        setCopied(false);
      }, 5000);
    })();
  }

  return (
    <button
      className={className}
      type="button"
      onClick={() => {
        handleCopy();
      }}
    >
      {copied ? <FontAwesomeIcon icon={faCheck} /> : children}
    </button>
  );
}
